/**
 * Phone values are deliberately kept as strings throughout the application.
 *
 * This module only presents numbers; it does not migrate or otherwise mutate
 * values stored in the database. US numbers are recognised only when there
 * are exactly ten digits, or eleven digits with a leading 1. Everything else
 * is returned without trying to guess its country or truncate it.
 */

const EXTENSION_RE = /\s*(?:ext(?:ension)?\.?|x)\s*([0-9]+)\s*$/i;

type ParsedPhone = {
  main: string;
  extension: string | null;
};

function parsePhone(value: string): ParsedPhone {
  const match = value.match(EXTENSION_RE);
  if (!match || match.index === undefined) {
    return { main: value, extension: null };
  }

  return {
    main: value.slice(0, match.index).trimEnd(),
    extension: match[1],
  };
}

function mainDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function isSafePhoneMain(value: string): boolean {
  return /^[\d\s().+\-]*$/.test(value);
}

function hasMalformedExtensionMarker(value: string): boolean {
  return /(?:ext(?:ension)?\.?|x)\s*$/i.test(value);
}

function isExplicitInternational(value: string): boolean {
  return /^\s*(?:\+|00)/.test(value);
}

function isExplicitUsInternational(value: string): boolean {
  return /^\s*\+1/.test(value);
}

function splitUsDigits(digits: string): string | null {
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  if (digits.length === 10) {
    return digits;
  }
  return null;
}

function formatUsDigits(digits: string): string {
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function formatExtension(extension: string | null): string {
  return extension ? ` ext. ${extension}` : "";
}

/**
 * Format a phone value for display or an input value.
 *
 * A leading US country code is removed once the complete number is present.
 * International and otherwise non-US lengths are intentionally returned as
 * entered; use formatPhoneInput while collecting a partial US number.
 */
export function formatPhone(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }

  const parsed = parsePhone(String(value));
  if (
    !isSafePhoneMain(parsed.main) ||
    hasMalformedExtensionMarker(parsed.main)
  ) {
    return String(value);
  }
  const digits = mainDigits(parsed.main);
  const usDigits = splitUsDigits(digits);

  // Explicit international numbers must not accidentally be interpreted as
  // US partial input (for example, +44 while it is being typed).
  if (
    (usDigits !== null && !isExplicitInternational(parsed.main)) ||
    (usDigits !== null && isExplicitUsInternational(parsed.main))
  ) {
    return `${formatUsDigits(usDigits)}${formatExtension(parsed.extension)}`;
  }

  return parsed.extension
    ? `${parsed.main} ext. ${parsed.extension}`
    : String(value);
}

/**
 * Format a phone while it is being edited. This is separate from
 * formatPhone to make the intended partial-input behaviour explicit to
 * callers and tests.
 */
export function formatPhoneInput(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }

  const parsed = parsePhone(String(value));
  if (
    !isSafePhoneMain(parsed.main) ||
    hasMalformedExtensionMarker(parsed.main)
  ) {
    return String(value);
  }
  const digits = mainDigits(parsed.main);
  const usDigits = splitUsDigits(digits);
  if (
    (usDigits !== null && !isExplicitInternational(parsed.main)) ||
    (usDigits !== null && isExplicitUsInternational(parsed.main))
  ) {
    return `${formatUsDigits(usDigits)}${formatExtension(parsed.extension)}`;
  }

  // Keep a partially entered international number untouched. For a plain
  // input, however, punctuation gives the person typing a predictable US
  // mask, including while the number is still incomplete.
  if (
    !isExplicitInternational(parsed.main) &&
    digits.length > 0 &&
    digits.length < 10
  ) {
    return `${formatUsDigits(digits)}${formatExtension(parsed.extension)}`;
  }

  return parsed.extension
    ? `${parsed.main} ext. ${parsed.extension}`
    : String(value);
}

/**
 * Return a tel: URI payload containing only dialable digits, a leading +, and
 * a standards-compatible extension suffix. Callers can use
 * `href={"tel:" + normalizePhoneHref(phone)}`.
 */
export function normalizePhoneHref(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return "";
  }

  const parsed = parsePhone(String(value).trim());
  const rawMain = parsed.main;
  const digits = mainDigits(rawMain);
  if (!digits) {
    return "";
  }

  let dialable: string;
  if (digits.length === 10) {
    dialable = `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith("1")) {
    dialable = `+${digits}`;
  } else if (/^\s*\+/.test(rawMain)) {
    dialable = `+${digits}`;
  } else if (/^\s*00/.test(rawMain)) {
    dialable = `+${digits.slice(2)}`;
  } else {
    // Do not infer a country for other lengths. Keeping the digits preserves
    // the existing dialling behaviour better than dropping or reordering
    // them.
    dialable = digits;
  }

  return parsed.extension ? `${dialable};ext=${parsed.extension}` : dialable;
}

/**
 * Digits used by phone search and duplicate checks. Unlike display
 * formatting, this intentionally includes all digits and never drops a
 * non-US country code.
 */
export function phoneSearchDigits(value: string | null | undefined): string {
  return value == null ? "" : String(value).replace(/\D/g, "");
}

/**
 * Keep the caret next to the same digit after punctuation is inserted or
 * removed. The returned position is suitable for setSelectionRange.
 */
export function phoneCaretPosition(
  rawValue: string,
  formattedValue: string,
  rawPosition: number,
): number {
  const boundedPosition = Math.max(0, Math.min(rawPosition, rawValue.length));
  // Values that the formatter deliberately leaves alone include international
  // numbers and incomplete/malformed extension suffixes. In that case there
  // is no punctuation delta to map, so preserving the exact caret position is
  // more reliable than counting digits (which can move a caret before a space
  // or a malformed extension marker).
  if (rawValue === formattedValue) {
    return boundedPosition;
  }
  const digitsBeforeCaret = phoneSearchDigits(rawValue.slice(0, boundedPosition)).length;
  if (digitsBeforeCaret === 0) {
    return 0;
  }

  let seenDigits = 0;
  for (let index = 0; index < formattedValue.length; index += 1) {
    if (/\d/.test(formattedValue[index])) {
      seenDigits += 1;
      if (seenDigits === digitsBeforeCaret) {
        return index + 1;
      }
    }
  }
  return formattedValue.length;
}
